import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiGetGrupos } from "../../api/gruposApi";
import { apiGetSubGrupos } from "../../api/subGruposApi";
import {
  apiGetAvailableExcludedQuestions,
  apiDeleteExcludedQuestionBySubgroup,
  apiGetExcludedQuestionsBySubgroup,
  apiSaveExcludedQuestionsBySubgroup,
} from "../../api/masters/excludedQuestionsApi";

export const useExcludedQuestions = () => {
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [relations, setRelations] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedSubgroup, setSelectedSubgroup] = useState("");
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [loadingRelations, setLoadingRelations] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasLoadedRelations, setHasLoadedRelations] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      const res = await apiGetGrupos("");
      const activeGroups = (res.data || []).filter((group) => String(group.estado) === "1");
      setGroups(activeGroups);
    } catch {
      toast.error("Error cargando grupos");
    }
  }, []);

  const loadSubgroups = useCallback(async (groupId) => {
    if (!groupId) {
      setSubgroups([]);
      return;
    }

    try {
      const res = await apiGetSubGrupos("", groupId);
      const activeSubgroups = (res.data || []).filter((subgroup) => String(subgroup.estado) === "1");
      setSubgroups(activeSubgroups);
    } catch {
      toast.error("Error cargando subgrupos");
    }
  }, []);

  const loadQuestions = useCallback(async (subgroupId) => {
    try {
      setLoadingQuestions(true);
      const res = await apiGetAvailableExcludedQuestions();
      setQuestions(res.data || []);
    } catch {
      toast.error("Error cargando preguntas");
    } finally {
      setLoadingQuestions(false);
    }
  }, []);

  const loadRelations = useCallback(async (groupId = "", subgroupId = "") => {
    if (!subgroupId) {
      setRelations([]);
      setHasLoadedRelations(false);
      return;
    }

    try {
      setLoadingRelations(true);
      const res = await apiGetExcludedQuestionsBySubgroup(groupId, subgroupId);
      setRelations(res.data || []);
      setHasLoadedRelations(true);
    } catch {
      toast.error("Error cargando relaciones configuradas");
    } finally {
      setLoadingRelations(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (!selectedSubgroup) {
      setRelations([]);
      setHasLoadedRelations(false);
      return;
    }

    loadRelations(selectedGroup, selectedSubgroup);
  }, [loadRelations, selectedGroup, selectedSubgroup]);

  useEffect(() => {
    loadSubgroups(selectedGroup);
  }, [loadSubgroups, selectedGroup]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleGroupChange = (groupId) => {
    setSelectedGroup(groupId);
    setSelectedSubgroup("");
    setSelectedQuestionIds([]);
    setRelations([]);
    setHasLoadedRelations(false);
  };

  const handleSubgroupChange = (subgroupId) => {
    setSelectedSubgroup(subgroupId);
    setSelectedQuestionIds([]);
    if (!subgroupId) {
      setRelations([]);
      setHasLoadedRelations(false);
    }
  };

  const saveRelations = async () => {
    if (!selectedGroup) {
      toast.warn("Debe seleccionar un grupo");
      return;
    }

    if (!selectedSubgroup) {
      toast.warn("Debe seleccionar un subgrupo");
      return;
    }

    if (!selectedQuestionIds.length) {
      toast.warn("Debe seleccionar al menos una pregunta");
      return;
    }

    try {
      setSaving(true);

      const res = await apiSaveExcludedQuestionsBySubgroup({
        IdSubgrupo: Number(selectedSubgroup),
        Preguntas: selectedQuestionIds,
      });

      if (!res.success) {
        toast.error(res.message || "Error guardando relaciones");
        return;
      }

      const inserted = res.data?.insertados ?? 0;
      const skipped = res.data?.omitidos ?? 0;
      const detail = [];

      if (inserted > 0) detail.push(`${inserted} nuevas`);
      if (skipped > 0) detail.push(`${skipped} ya existentes`);

      toast.success(detail.length ? `Relaciones guardadas: ${detail.join(", ")}` : (res.message || "Relaciones guardadas"));
      setSelectedQuestionIds([]);
      loadRelations(selectedGroup, selectedSubgroup);
    } catch {
      toast.error("Error guardando relaciones");
    } finally {
      setSaving(false);
    }
  };

  const deleteRelation = async (relation) => {
    try {
      setDeleting(true);
      const res = await apiDeleteExcludedQuestionBySubgroup({
        IdSubgrupo: Number(relation.IdSubgrupo),
        IdPregunta: Number(relation.IdPregunta),
      });

      if (!res.success) {
        toast.error(res.message || "Error eliminando relacion");
        return;
      }

      toast.success(res.message || "Relacion eliminada correctamente");
      loadRelations(selectedGroup, selectedSubgroup);
    } catch {
      toast.error("Error eliminando relacion");
    } finally {
      setDeleting(false);
    }
  };

  return {
    groups,
    subgroups,
    questions,
    relations,
    selectedGroup,
    selectedSubgroup,
    selectedQuestionIds,
    loadingRelations,
    loadingQuestions,
    saving,
    deleting,
    hasLoadedRelations,
    setSelectedQuestionIds,
    handleGroupChange,
    handleSubgroupChange,
    saveRelations,
    deleteRelation,
  };
};
